import uniqBy from "lodash/uniqBy";

import {getCommonVisualizerType} from "../../config";
import {getLookList} from "../look/lookNavigation/lookFunctions";
import {getCurrentLayout} from "../appLayout";
import {loadThumbnail} from "./aiThumbnailsLoad";
import {FEATURE_AREAS} from "./aiFeatureAreasConstants";
import snakeCase from "lodash/snakeCase";

export const saveButtonIsEnabled = (popupContext) => {
  return !!popupContext.userFile && popupContext.promptsList && popupContext.promptsList.some(pr => pr.isActive);
}

export const imageIsRenderedAndUserIsNotLoggedIn = (showLeadOverlay, aiResults) => {
  return showLeadOverlay && aiResults.some(res => res.resultImageUrl);
}

export const toBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => {
    const dataUrl = reader.result;
    const base64Data = dataUrl.split(',')[1]; // ← This extracts just the image part
    resolve(base64Data);
  };
  reader.onerror = reject;
});

export const getExportAIData = (aiState, groupObjects, aiResults) => {
  const visualizerType = getCommonVisualizerType();
  let exportData = {
    ...aiState.aiExportData,
    aiResults: (aiResults || aiState.aiResults).map(result => {
      if (result.sourceImageUrl?.includes("base64")) {
        result.sourceImageUrl = ""
      }
      return {
        conversionError: result.conversionError,
        sourceImageUrl: result.sourceImageUrl,
        resultImageUrl: result.resultImageUrl,
        id: result.id,
        isActive: result.isActive,
      };
    })
  };
  if (Array.isArray(groupObjects) && groupObjects.length > 0) {
    exportData.aiGroups = {
      ...(exportData.aiGroups || {}),
      [visualizerType]: (groupObjects || []).map(groupObject => {
        return {
          partId: groupObject.partId,
          enabled: groupObject.enabled,
          groupName: groupObject.groupName || groupObject.featureGroup?.dbName || groupObject.featureGroup?.partName,
        }
      })
    }
  }
  // console.log("exportData", exportData);
  return exportData
}

export const hideGroupObject = (groupObject, showAllGroups = false) => {
  if (showAllGroups) {
    return !groupObject
  }
  return !groupObject
    || groupObject.hideIfShowAllIsNotEnabled
    || !groupObject.featureGroup
    || (groupObject.featureGroup.hideLayerCondition && groupObject.featureGroup.hideLayerCondition())
    || (groupObject.featureGroup.activeFeature && !groupObject.featureGroup.activeFeature.layer.src)
}

/**
 * Given the partIds that make up an AI feature area (e.g. "Wet Area"), returns only the
 * ones that are currently active in the layout - i.e. rendered as a layer. Shared between
 * the manual toggle in UiPopupAIContentFeaturesAreas and setDefaultAiFeatureAreasGroups so
 * both compute "which groups are active right now" the same way.
 */
export const getActivePartIdsForFeatureArea = (availablePartIds, layout) => {
  const layers = layout.getLayers();
  return availablePartIds.filter(partId => layers.some(l => l.partId === partId));
}

export const getAIInitialGroups = (aiImportData, aiSettings, forDevSettings = false) => {
  const lookList = getLookList();
  const layout = getCurrentLayout();
  const visualizerType = getCommonVisualizerType();

  const importAiGroups = aiImportData && aiImportData.aiGroups && aiImportData.aiGroups[visualizerType];
  const hasImportAiGroups = Array.isArray(importAiGroups) && importAiGroups.length > 0;

  const baseAiGroups = Array.isArray(aiSettings.aiGroups)
    ? aiSettings.aiGroups
    : typeof aiSettings.aiGroups === "object" && aiSettings.aiGroups ? aiSettings.aiGroups[visualizerType] || [] : [];

  // aiSettings.aiGroups (the fabricator-configured group list) decides *which* groups
  // exist, and takes priority over aiImportData.aiGroups[visualizerType] (a previously
  // saved job's group list) - except any import group missing from aiSettings.aiGroups
  // is still appended below, so it isn't lost. aiSettings.aiGroups may just be an array
  // of groupName strings, e.g. ['Countertop', 'Faucet'], so matching here is by groupName.
  const baseGroupNames = new Set(
    baseAiGroups.map(group => (group && typeof group === "object" ? group.groupName : group))
  );
  const missingImportGroups = hasImportAiGroups
    ? importAiGroups.filter(group => group && typeof group === "object" && !baseGroupNames.has(group.groupName))
    : [];

  const aiGroups = [...baseAiGroups, ...missingImportGroups];
  const groupsList = aiGroups.length ? aiGroups.map(group => {
    if (typeof group === "object") {
      const featureGroup = layout.getFeatureGroupByGroupName(group.groupName);
      if (featureGroup) {
        return {
          ...group,
          featureGroup,
        }
      }
    }
    const featureGroup = layout.getFeatureGroupByGroupName(group);
    if (featureGroup) {
      return {
        featureGroup,
        partId: featureGroup.partId,
      }
    }
  }).filter(groupName => groupName) : lookList;

  if (forDevSettings) {
    const allFetureGroups = layout.getAllFeatureGroups();
    const devGroups = allFetureGroups.map(featureGroup => {
      return {
        featureGroup,
        partId: featureGroup.partId,
        enabled: true,
        hideIfShowAllIsNotEnabled: !groupsList.some(group => group.partId === featureGroup.partId),
      }
    });

    // Synthetic groups for the additional feature areas (Wet Area, Vanity Area).
    // These have no real layer/activeFeature, they only exist so dev users can
    // attach a partBody prompt to the area as a whole. The featureGroup name
    // mirrors the `featureArea` from FEATURE_AREAS and partId is its lowercase.
    const isBathroom = getCommonVisualizerType() === "bathroom";
    const customAreaGroups = isBathroom ? FEATURE_AREAS.map(({featureArea}) => {
      const partId = snakeCase(featureArea);
      return {
        featureGroup: {
          partId,
          partName: featureArea,
          dbName: featureArea,
        },
        partId,
        enabled: true,
        hideIfShowAllIsNotEnabled: false,
      };
    }) : [];

    return [...customAreaGroups, ...devGroups];
  }

  // When aiImportData.aiGroups[visualizerType] is present, it's the sole source of
  // truth for `enabled`: groups it lists come through with its enabled value, any
  // group it doesn't list comes through disabled.
  const importEnabledByPartId = hasImportAiGroups
    ? new Map(
      importAiGroups
        .filter(group => group && typeof group === "object" && group.partId != null)
        .map(group => [group.partId, group.enabled])
    )
    : null;

  return uniqBy(groupsList, (item) => item.partId)
    .filter(groupObject => groupObject?.featureGroup?.activeFeature)
    .map(groupObject => {
      groupObject.enabled = forDevSettings
        ? true
        : importEnabledByPartId
          ? importEnabledByPartId.has(groupObject.partId) ? importEnabledByPartId.get(groupObject.partId) : false
          : typeof groupObject.enabled === 'boolean' ? groupObject.enabled : true;
      return groupObject
    });
}

/**
 * @param onlyRenderGroups {array}
 * @param aiSendOnlySelectedGroupsAsAReference {boolean}
 * @param featureGroupsWithEnabledThumbnails {featureGroupInterface[]}
 * */
export const createThumbnailsCanvas = async (onlyRenderGroups = [],
                                             aiSendOnlySelectedGroupsAsAReference,
                                             featureGroupsWithEnabledThumbnails = null) => {
  const layout = getCurrentLayout();
  let layers = layout.getLayers(false, true);
  // console.log("layers before", layers);
  if (featureGroupsWithEnabledThumbnails) {
    layers = layers.filter(layer => featureGroupsWithEnabledThumbnails.some(fg => (fg.partId === layer.partId)));
  }
  // console.log("layers featureGroupsWithEnabledThumbnails", featureGroupsWithEnabledThumbnails, layers);
  if (Array.isArray(onlyRenderGroups) && onlyRenderGroups.length) {
    if (!featureGroupsWithEnabledThumbnails) { // we already checked
      layers = layers.filter(layer => {
        return !onlyRenderGroups.some(fg => (fg.partId === layer.partId) && fg.sendThumbnail === false)
      });
      // console.log("layers after onlyRenderGroups", layers);
    }
    if (aiSendOnlySelectedGroupsAsAReference) {
      layers = layers.filter(layer => onlyRenderGroups.some(fg => fg.partId === layer.partId));
    }
  }
  // console.log("layers final", layers);
  const allFeatureGroupActiveFeatures = layout.getActiveFeatures(true);
  const currentActiveFeatures = allFeatureGroupActiveFeatures.filter(f => {
    return f.cartFeature && layers.some(l => l.partId === f.partId)
  });
  console.log("currentActiveFeatures", currentActiveFeatures);

  const COLS = 4;
  const MARGIN = 20;
  const LABEL_HEIGHT = 48;
  const FONT_SIZE = 17;

  // Load all thumbnails first so we know their actual sizes
  const loadedImages = await Promise.all(
    currentActiveFeatures.map(async (f) => {
      try {
        return await loadThumbnail(f.cartFeature);
      } catch (e) {
        console.warn("[createThumbnailsCanvas] failed to load thumbnail for feature:", f.cartFeature, e);
        return null;
      }
    })
  );

  // Determine cell size from the largest loaded thumbnail
  const maxThumbW = loadedImages.reduce((max, img) => img ? Math.max(max, img.naturalWidth) : max, 0);
  const maxThumbH = loadedImages.reduce((max, img) => img ? Math.max(max, img.naturalHeight) : max, 0);
  const THUMB_W = maxThumbW || 400;
  const THUMB_H = maxThumbH || 400;

  const cols = Math.min(COLS, currentActiveFeatures.length);
  const rows = Math.ceil(currentActiveFeatures.length / COLS);
  const cellWidth = THUMB_W + MARGIN * 2;
  const cellHeight = THUMB_H + MARGIN * 2 + LABEL_HEIGHT;

  // 1) create canvas sized to content
  const canvas = document.createElement('canvas');
  canvas.width = cols * cellWidth;
  canvas.height = rows * cellHeight;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // 3) draw each thumbnail on the canvas in a grid, with label below
  for (let i = 0; i < currentActiveFeatures.length; i++) {
    const activeFeature = currentActiveFeatures[i];
    const col = i % COLS;
    const row = Math.floor(i / COLS);

    const cellX = col * cellWidth;
    const cellY = row * cellHeight;

    const img = loadedImages[i];
    if (img) {
      ctx.drawImage(img, cellX + MARGIN, cellY + MARGIN, THUMB_W, THUMB_H);
    }

    const label = activeFeature.featureGroup?.dbName || activeFeature.featureGroup?.partName || '';
    ctx.fillStyle = '#000000';
    ctx.font = `bold ${FONT_SIZE}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(label, cellX + MARGIN + Math.floor(THUMB_W / 2), cellY + MARGIN + THUMB_H + FONT_SIZE + 4, THUMB_W);
  }

  // 4) get imageData in the same format as getImagePromiseFromLayout() returns
  const imageData = canvas.toDataURL('image/jpeg', 1);

  // const response = await fetch(imageData);
  // const blob = await response.blob();
  // const url = URL.createObjectURL(blob);
  // window.open(url, '_blank');

  return {
    "b64File": imageData.split(',')[1],
    "fileName": "productGrid.jpg"
  }
}
